// LLM generated - Model Claude Opus 4
/**
 * useContestMessagesState - Messages (bidirectional chat) state management
 *
 * Handles:
 * - Messages infinite query
 * - Unread messages tracking
 * - Last read message tracking
 * - Optimistic message updates
 * - Send message action
 * - Reaction actions for messages
 *
 * Uses shared utilities from useContestChatUtils for deduplication and caching.
 */
import { useCallback } from 'react';
import { showNotification } from '@mantine/notifications';
import useTranslation from 'next-translate/useTranslation';

import { useOptimistic } from '~/common/hooks/useOptimistic';
import { logError } from '~/common/utils/log';
import {
  sendMessage as sendMessageAPI,
  addReaction as addReactionAPI,
  removeReaction as removeReactionAPI,
} from '~/services/live-activity/live-activity-service.service.api';
import type {
  ChatMessage,
  ChatMessagesInfiniteData,
  SendMessagePayload,
  AddReactionPayload,
  Chat,
} from '~/services/live-activity/types';
import { useChatMessagesInfinite } from '~/services/live-activity/hooks/useChatMessagesInfinite';
import { useUnreadMessages } from '~/services/live-activity/hooks/useUnreadMessages';
import { useLastReadMessage } from '~/services/live-activity/hooks/useLastReadMessage';

import { MESSAGE_PAGE_SIZE } from '../constants/queryKeys';

import { applyOptimisticMessage, type ContestChatUtils } from './useContestChatUtils';

type UseContestMessagesStateProps = {
  messagesChat: Chat | undefined;
  user: {
    id: string;
    handle?: string;
    avatarUrl?: string;
  } | null;
  isChatOpen: boolean;
  isChatEnabled: boolean;
  utils: ContestChatUtils;
};

export function useContestMessagesState({
  messagesChat,
  user,
  isChatOpen,
  isChatEnabled,
  utils,
}: UseContestMessagesStateProps) {
  const { t } = useTranslation('chat');

  const {
    queryClient,
    messagesQueryKey,
    generateOptimisticId,
    trackPendingMessage,
    clearPendingMessage,
    expectEventRef,
    trackPendingReaction,
    checkAndClearPendingReaction,
  } = utils;

  // --- Queries ---

  const {
    messages: serverMessagesChatHistory,
    fetchNextPage: fetchNextPageMessages,
    hasNextPage: hasNextPageMessages,
    isFetchingNextPage: isFetchingNextPageMessages,
    isLoading: isLoadingMessagesChat,
    error: messagesChatError,
    refetch: refetchMessagesChat,
  } = useChatMessagesInfinite({
    chatId: messagesChat?.id,
    enabled: !!messagesChat?.id && messagesChat?.isEnabled && isChatOpen,
    pageSize: MESSAGE_PAGE_SIZE,
  });

  const { data: unreadMessagesData, refetch: refetchUnreadMessages } = useUnreadMessages({
    chatId: messagesChat?.id,
    enabled: isChatEnabled && !!user && !!messagesChat?.id,
  });

  const { markAsRead: markAsReadMessages, getLastSentMessageId: getLastSentMessagesMessageId } =
    useLastReadMessage({ chatId: messagesChat?.id });

  // --- Optimistic State ---

  const [
    optimisticMessagesChatHistory,
    addOptimisticMessage,
    clearOptimisticMessages,
    isMessagesPending,
  ] = useOptimistic(serverMessagesChatHistory, applyOptimisticMessage);

  // --- Actions ---

  const sendChatMessage = useCallback(
    async (params: { message: string; header?: string }) => {
      if (!messagesChat?.id || !user?.handle) {
        logError('Cannot send message: missing chatId or user handle');
        return;
      }

      const optimisticId = generateOptimisticId();

      const optimisticMsg: ChatMessage = {
        id: optimisticId,
        publishedAt: new Date().toISOString(),
        payload: { message: params.message, header: params.header },
        user: { id: user.id, handle: user.handle, avatarUrl: user.avatarUrl },
        reactions: [],
        userReactions: [],
        updatedAt: new Date().toISOString(),
        isPinned: false,
        splashMessage: false,
      };

      trackPendingMessage(optimisticId, params.message, 'bidirectional');
      addOptimisticMessage({ type: 'add', message: optimisticMsg });
      expectEventRef.current?.('message-created', 'bidirectional');

      try {
        const payload: SendMessagePayload = {
          message: params.message,
          handle: user.handle || '',
          header: params.header,
          avatarUrl: user.avatarUrl,
        };
        await sendMessageAPI(messagesChat.id, payload);
      } catch (error) {
        clearPendingMessage(optimisticId);
        await queryClient.invalidateQueries({ queryKey: messagesQueryKey });
        showNotification({
          title: t('notifications.error'),
          message: t('notifications.messageSendFailed'),
          color: 'red',
        });
      }
    },
    [
      messagesChat?.id,
      user,
      generateOptimisticId,
      trackPendingMessage,
      addOptimisticMessage,
      clearPendingMessage,
      queryClient,
      messagesQueryKey,
      expectEventRef,
      t,
    ]
  );

  const handleAddReaction = useCallback(
    async (messageId: string, reactionCode: string) => {
      if (!messagesChat?.id || !user?.handle) {
        logError('Cannot add reaction: missing chatId or user handle');
        return;
      }

      trackPendingReaction(messageId, reactionCode, 'add');

      // Optimistic update
      queryClient.setQueryData<ChatMessagesInfiniteData>(messagesQueryKey, (oldData) => {
        if (!oldData?.pages) return oldData;

        const newPages = oldData.pages.map((page) => ({
          ...page,
          data: (page.data || []).map((msg) => {
            if (msg.id !== messageId) return msg;

            const reactions = [...msg.reactions];
            const existingIdx = reactions.findIndex((r) => r.code === reactionCode);

            if (existingIdx >= 0) {
              reactions[existingIdx] = {
                ...reactions[existingIdx],
                count: reactions[existingIdx].count + 1,
              };
            } else {
              reactions.push({ code: reactionCode, count: 1 });
            }

            const userReactions = [...(msg.userReactions || [])];
            if (!userReactions.includes(reactionCode)) {
              userReactions.push(reactionCode);
            }

            return { ...msg, reactions, userReactions };
          }),
        }));

        return { ...oldData, pages: newPages };
      });

      expectEventRef.current?.('reaction-added', 'bidirectional');

      try {
        const payload: AddReactionPayload = {
          code: reactionCode,
          handle: user.handle || '',
          ...(user.avatarUrl && { avatarUrl: user.avatarUrl }),
        };
        await addReactionAPI(messagesChat.id, messageId, payload);
      } catch (error) {
        checkAndClearPendingReaction(messageId, reactionCode, 'add');
        await queryClient.invalidateQueries({ queryKey: messagesQueryKey });

        logError('Failed to add reaction:', error);
        showNotification({
          title: t('notifications.error'),
          message: t('notifications.messageSendFailed'),
          color: 'red',
          autoClose: 5000,
        });
      }
    },
    [
      messagesChat?.id,
      user,
      queryClient,
      messagesQueryKey,
      trackPendingReaction,
      checkAndClearPendingReaction,
      expectEventRef,
      t,
    ]
  );

  const handleRemoveReaction = useCallback(
    async (messageId: string, reactionCode: string) => {
      if (!messagesChat?.id) {
        logError('Cannot remove reaction: missing chatId');
        return;
      }

      trackPendingReaction(messageId, reactionCode, 'remove');

      // Optimistic update
      queryClient.setQueryData<ChatMessagesInfiniteData>(messagesQueryKey, (oldData) => {
        if (!oldData?.pages) return oldData;

        const newPages = oldData.pages.map((page) => ({
          ...page,
          data: (page.data || []).map((msg) => {
            if (msg.id !== messageId) return msg;

            let reactions = [...msg.reactions];
            const existingIdx = reactions.findIndex((r) => r.code === reactionCode);

            if (existingIdx >= 0) {
              if (reactions[existingIdx].count <= 1) {
                reactions = reactions.filter((r) => r.code !== reactionCode);
              } else {
                reactions[existingIdx] = {
                  ...reactions[existingIdx],
                  count: reactions[existingIdx].count - 1,
                };
              }
            }

            const userReactions = (msg.userReactions || []).filter((code) => code !== reactionCode);

            return { ...msg, reactions, userReactions };
          }),
        }));

        return { ...oldData, pages: newPages };
      });

      expectEventRef.current?.('reaction-removed', 'bidirectional');

      try {
        await removeReactionAPI(messagesChat.id, messageId, reactionCode);
      } catch (error) {
        checkAndClearPendingReaction(messageId, reactionCode, 'remove');
        await queryClient.invalidateQueries({ queryKey: messagesQueryKey });
        logError('Failed to remove reaction:', error);
        showNotification({
          title: t('notifications.error'),
          message: t('notifications.messageSendFailed'),
          color: 'red',
          autoClose: 5000,
        });
      }
    },
    [
      messagesChat?.id,
      queryClient,
      messagesQueryKey,
      trackPendingReaction,
      checkAndClearPendingReaction,
      expectEventRef,
      t,
    ]
  );

  return {
    // Messages data
    messagesChatHistory: optimisticMessagesChatHistory,
    serverMessagesChatHistory,
    isLoadingMessagesChat,
    messagesChatError,
    refetchMessagesChat,

    // Infinite scroll
    fetchNextPageMessages,
    hasNextPageMessages,
    isFetchingNextPageMessages,

    // Unread
    unreadMessagesData,
    refetchUnreadMessages,

    // Last read
    markAsReadMessages,
    getLastSentMessagesMessageId,

    // Actions
    sendChatMessage,
    handleAddReaction,
    handleRemoveReaction,

    // Optimistic state
    clearOptimisticMessages,
    isMessagesPending,
  };
}

export type ContestMessagesState = ReturnType<typeof useContestMessagesState>;
